import React from "react";
import lottie from "lottie-web";
import PropTypes from "prop-types";
import cname from "./style.module.scss";
import {
  EVENT,
  RANGE_INPUT,
  LOAD_ANIMATION_CONFIG,
} from "./LottiePlayer.constants";
import { Icons } from "@learningblocks/exp-constants";

const {
  StartSolid: PlayIcon,
  StopSolid: StopIcon,
  PauseSolid: PauseIcon,
} = Icons;

/**
 * LottierPlayer
 *  - A wrapper over the lottie-web library
 *  - Function as a Lottie Animation file player
 *  - Has options to pause/play and stop playback of animation file
 *  - Also has a slider that could be used to move forward  and backward in playback
 */
export class LottiePlayer extends React.Component {
  constructor(props) {
    super(props);
    this.elementRef = React.createRef();
    this.state = {
      anime: null,
      //currentFrame: props.currentFrame,
    };
  }

  setCurrentFrame(currentFrame) {
    this.setState({ currentFrame });
  }

  componentDidMount() {
    this.setAnime();
  }

  addAllEvents = (anime) => {
    const { loop, stopOnLastFrame } = this.props;
    const shouldLoop = loop && !stopOnLastFrame;

    anime.addEventListener(EVENT.ENTER_FRAME, this.listenEnterFrame);
    if (this.props.path) {
      anime.addEventListener(EVENT.DATA_READY, this.animationLoaded);
    }

    if (shouldLoop) {
      anime.addEventListener(
        EVENT.LOOP_COMPLETE,
        this.playbackCompleted(this.props.playbackCompleted)
      );
    } else {
      anime.addEventListener(
        EVENT.COMPLETE,
        this.playbackCompleted(this.props.playbackCompleted)
      );
    }
  };

  removeAllEvents = () => {
    this.state.anime.removeEventListener(
      EVENT.ENTER_FRAME,
      this.listenEnterFrame
    );
    if (this.props.path) {
      this.state.anime.removeEventListener(
        EVENT.DATA_READY,
        this.animationLoaded
      );
    }
    if (this.props.loop) {
      this.state.anime.removeEventListener(
        EVENT.LOOP_COMPLETE,
        this.playbackCompleted(this.props.playbackCompleted)
      );
    } else {
      this.state.anime.removeEventListener(
        EVENT.COMPLETE,
        this.playbackCompleted(this.props.playbackCompleted)
      );
    }
  };

  componentDidUpdate(prevProps) {
    if (
      prevProps.animationData !== this.props.animationData ||
      prevProps.path !== this.props.path ||
      prevProps.loop !== this.props.loop
    ) {
      this.resetAnime();
      this.setAnime();
    }

    if (prevProps.currentFrame !== this.props.currentFrame) {
      this.goToFrame(this.props.currentFrame);
    }
  }

  setAnime = () => {
    const anime = getLottieInstance(this.props, this.elementRef.current);
    this.addAllEvents(anime);
    if (this.props.path) {
      this.setState({ anime });
    } else {
      this.setState({ anime }, this.animationLoaded);
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  playbackCompleted =
    (
      callback = () => {
        // Intentionally left blank
      }
    ) =>
    (event) => {
      const { stopOnLastFrame } = this.props;
      const anime = this.state.anime;

      if (event.type === EVENT.COMPLETE && anime) {
        if (stopOnLastFrame && typeof anime.totalFrames === "number") {
          // Jump to the final frame and hold
          anime.goToAndStop(anime.totalFrames - 1, true);
        } else {
          // Default behaviour: just stop wherever we are
          anime.stop();
        }
      }

      callback(event);
    };

  animationLoaded = () => {
    if (this.props.animationLoaded && this.state.anime) {
      const {
        currentFrame,
        anime: { firstFrame, totalFrames },
      } = this.state;
      this.goToFrame(currentFrame);
      this.props.animationLoaded({ firstFrame, totalFrames, currentFrame });
      if (this.props.autoplay) {
        this.state.anime.play();
      }
    }
  };

  resetAnime = () => {
    if (this.state.anime) {
      this.removeAllEvents();
      this.state.anime.destroy();
    }
    this.setState({ anime: null });
  };

  componentWillUnmount() {
    this.resetAnime();
  }

  listenEnterFrame = ({ currentTime }) => {
    this.setCurrentFrame(currentTime);
  };

  goToFrame = (frameNumber) => {
    if (this.state.anime) {
      this.state.anime.goToAndStop(frameNumber, true);
    }
  };

  sliderOnChange = (event) => {
    const currentFrame = event.target.valueAsNumber;
    this.goToFrame(currentFrame);
  };

  onClickPlay = () => this.state.anime?.play();

  onClickPause = () => {
    if (this.state.anime) {
      this.state.anime.pause();
      // to force a render.
      this.setCurrentFrame(this.state.anime.currentFrame);
    }
  };

  onClickStop = () => this.state.anime?.stop();

  renderPausePlayButton = () =>
    this.state.anime?.isPaused ? (
      <span
        className={cname.icon}
        onClick={this.onClickPlay}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            this.onClickPlay();
          }
        }}
        role="button"
        tabIndex={0}
      >
        <PlayIcon />
      </span>
    ) : (
      <span
        className={cname.icon}
        onClick={this.onClickPause}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            this.onClickPause();
          }
        }}
        role="button"
        tabIndex={0}
        style={{ cursor: "pointer" }}
      >
        <PauseIcon />
      </span>
    );

  showContorls = () => this.props.showContorls && this.state.anime;

  render() {
    const wrapperClass = `${cname.player} ${this.props.className}`;
    return (
      <div
        style={this.props.style}
        tabIndex={this.props.tabIndex}
        className={wrapperClass}
      >
        <div
          aria-hidden="true"
          ref={this.elementRef}
          className={cname.animation}
          style={{
            ...this.props.elemStyle,
            background: this.props.animationBackground
              ? this.props.animationBackground
              : "#003f6f",
          }}
        />
        {this.showContorls() && (
          <div className={cname.controls}>
            {this.renderPausePlayButton()}
            <span
              className={cname.icon}
              onClick={this.onClickStop}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  this.onClickStop();
                }
              }}
              role="button"
              tabIndex={0}
              style={{ cursor: "pointer" }}
            >
              <StopIcon />
            </span>
            <span className={cname.range}>
              <input
                type={RANGE_INPUT}
                min={this.state.anime?.firstFrame || 0}
                max={this.state.anime?.totalFrames || 0}
                value={this.state.currentFrame}
                onChange={this.sliderOnChange}
              />
            </span>
          </div>
        )}
      </div>
    );
  }
}

LottiePlayer.propTypes = {
  /**
   * Set to play animation in loop
   */
  loop: PropTypes.bool,
  /**
   * Set to autoplay the animation
   */
  autoplay: PropTypes.bool,
  /**
   * Url to remove animation file
   */
  path: PropTypes.string,
  /**
   * Set to show controls
   */
  showContorls: PropTypes.bool,
  /**
   * currentFrame to display on the animation
   */
  currentFrame: PropTypes.number,
  /**
   * Callback fired when animation is loaded
   */
  animationLoaded: PropTypes.func,
  /**
   * Callback fired when animation Complete one iteration
   */
  playbackCompleted: PropTypes.func,
  /**
   * Style to set on player wrapper
   */
  style: PropTypes.object,
  /**
   * Style to set on animation element
   */
  elemStyle: PropTypes.object,
  stopOnLastFrame: PropTypes.bool,
};

LottiePlayer.defaultProps = {
  tabIndex: null,
  path: undefined,
  style: {},
  loop: false,
  className: "",
  elemStyle: {},
  currentFrame: 0,
  autoplay: false,
  showContorls: true,
  animationData: undefined,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  animationLoaded: () => {},
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  playbackCompleted: () => {},
  stopOnLastFrame: false,
};

/**
 * Function to create and instance of Animation
 */
function getLottieInstance(params, container) {
  // If stopOnLastFrame is true, we disable looping even if loop=true
  const effectiveLoop = params.loop && !params.stopOnLastFrame;

  if (params.path) {
    // Load from a URL
    return lottie.loadAnimation({
      ...LOAD_ANIMATION_CONFIG,
      loop: effectiveLoop,
      autoplay: params.autoplay,
      container,
      path: params.path,
    });
  } else {
    // If there are external images to patch into the animationData
    if (params.animationImages) {
      params.animationImages.map((image) => {
        const filename = image.filename.substring(0, image.filename.length - 4);
        params.animationData.assets.map((asset) => {
          const name = asset.id.replace("image_", "img_");
          if (filename === name) {
            asset.u = "";
            asset.p = image.url;
          }
        });
      });
    }

    // Load from a preloaded JSON object
    return lottie.loadAnimation({
      ...LOAD_ANIMATION_CONFIG,
      loop: effectiveLoop,
      autoplay: params.autoplay,
      container,
      animationData: params.animationData,
    });
  }
}
